import { useMemo } from "react";
import { DataTable } from "@/components/data-table/data-table";
import { DataTableSkeleton } from "@/components/data-table/data-table-skeleton";
import { useDataTable } from "@/lib/hooks/use-data-table";
import { useExportHistory } from "@/lib/api/exports";
import { columns } from "./columns";
import { useRouter, useSearch } from "@tanstack/react-router";

export function ExportHistoryTable() {
  const router = useRouter();
  const { page, per_page } = useSearch({
    strict: false,
  }) as Record<string, string>;

  const currentPage = parseInt(page || "1", 10);
  const pageSize = parseInt(per_page || "10", 10);

  const {
    data: exportHistory,
    isPending,
    isError,
  } = useExportHistory(currentPage, pageSize);

  const data = useMemo(() => exportHistory?.exports || [], [exportHistory]);

  const { table } = useDataTable({
    data,
    columns,
    pageCount: -1, // Infinite pagination
    defaultPerPage: pageSize,
  });

  const handlePageChange = (newPageIndex: number) => {
    const newPage = newPageIndex + 1;
    router.navigate({
      to: router.state.location.pathname,
      search: (prev: any) => ({
        ...prev,
        page: newPage,
      }),
    });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    router.navigate({
      to: router.state.location.pathname,
      search: (prev: any) => ({
        ...prev,
        page: 1, // Reset to first page when changing page size
        per_page: newPageSize,
      }),
    });
  };

  if (isPending) {
    return (
      <DataTableSkeleton
        columnCount={6}
        rowCount={5}
        searchableColumnCount={0}
        filterableColumnCount={0}
        showViewOptions={false}
      />
    );
  }

  if (isError) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-4">
        <p className="text-sm text-red-700">
          Failed to load export history. Please try again.
        </p>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="rounded-md border border-gray-200 bg-gray-50 p-8">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900">No exports yet</h3>
          <p className="mt-2 text-sm text-gray-500">
            Get started by requesting your first NCDHHS export above.
          </p>
        </div>
      </div>
    );
  }

  return (
    <DataTable
      table={table}
      isPending={isPending}
      getCanNextPage={() => {
        return !exportHistory?.pagination.isLastPage;
      }}
      getCanPreviousPage={() => {
        return currentPage > 1;
      }}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
    />
  );
}
